// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { PopoverContextProps } from '@iheartjane/reefer';
import {
  Box,
  Button,
  ChevronDownIcon,
  Flex,
  Link,
  List,
  Loading,
  Popover,
  Typography,
} from '@iheartjane/reefer';
import sortBy from 'lodash-es/sortBy';
import { useMemo } from 'react';
import { useLocation, useMatches } from 'react-router';

import {
  useIpadMediaQuery,
  useKioskSpacing,
  useThemeGenerator,
} from '@jane/kiosk/hooks';
import { KioskThemeProvider, useSearchContext } from '@jane/kiosk/providers';
import {
  getSource,
  hasFilterableOptions,
  trackFilteredProductList,
} from '@jane/kiosk/util';
import type { ActiveFilters, FilterSelectItem } from '@jane/search/data-access';
import type { Store } from '@jane/shared/types';

import { FilterCheckbox } from '../FilterSection/FilterCheckbox';

export interface QuickFilterButtonProps {
  activeFilters: ActiveFilters[];
  filteredProductCount: number;
  label: string;
  onClearAll: (label: string) => void;
  onOptionSelect: (option: FilterSelectItem) => void;
  options: FilterSelectItem[];
  store: Store;
  totalProductCount: number;
}

interface QuickFilterItemProps extends Pick<
  QuickFilterButtonProps,
  'onOptionSelect'
> {
  checked: boolean;
  disabled: boolean;
  isLastItem: boolean;
  option: FilterSelectItem;
}

const StyledPopoverContent = styled.div(() => {
  return {
    '::-webkit-scrollbar': {
      display: 'none',
    },
    minWidth: '339px',
    maxHeight: 560,
    overflowX: 'hidden' as const,
    overflowY: 'auto' as const,
  };
});

const StyledList = styled(List)(() => {
  const isIpad = useIpadMediaQuery();
  const lastItemMargin = 16;
  const footerHeight = (isIpad ? 48 : 64) + lastItemMargin;

  return {
    'li:last-child': {
      marginBottom: footerHeight,
    },
  };
});

const Footer = styled(Flex)(({ theme }) => {
  const isIpad = useIpadMediaQuery();
  const paddingBottom = 24;

  return {
    backgroundColor: theme.colors.grays.white,
    bottom: 0,
    left: 0,
    marginTop: 4,
    paddingBottom,
    paddingLeft: 24,
    paddingRight: 24,
    maxHeight: (isIpad ? 48 : 64) + paddingBottom,
    position: 'absolute' as const,
    right: 0,
  };
});

const QuickFilterItem = ({
  checked,
  disabled,
  onOptionSelect,
  option,
}: QuickFilterItemProps) => {
  return (
    <List.Item px={24} py={12}>
      <FilterCheckbox
        checkboxSpacing={8}
        checked={checked}
        disabled={disabled}
        item={option}
        onChange={(isChecked) => {
          if (checked !== isChecked) {
            onOptionSelect(option);
          }
        }}
        showCount={false}
      />
    </List.Item>
  );
};

export const QuickFilterButton = ({
  activeFilters,
  label,
  onClearAll,
  onOptionSelect,
  options,
  totalProductCount,
  filteredProductCount,
  store,
}: QuickFilterButtonProps) => {
  const { isIpad } = useKioskSpacing();
  const storeTheme = useThemeGenerator(store?.custom_theme_color ?? '#5E1FF0');
  const { pathname } = useLocation();
  const matches = useMatches();
  const { searchState } = useSearchContext();
  const isDisabled = useMemo(() => !hasFilterableOptions(options), [options]);

  return (
    <Popover
      alignment={{
        horizontal: 'left',
        vertical: 'bottom',
      }}
      disableMobileStyling
      label={label}
      openOn={isDisabled ? 'disabled' : 'click'}
      target={
        <Button
          disabled={isDisabled}
          endIcon={<ChevronDownIcon size="sm" />}
          mr={16}
          label={
            <Typography
              color={activeFilters?.length ? 'text-info' : 'text-main'}
              variant="body-semibold"
            >
              {label}
            </Typography>
          }
          size={isIpad ? 'small' : 'default'}
          variant={activeFilters?.length ? 'primary' : 'tertiary'}
        />
      }
    >
      {({ closePopover }: PopoverContextProps) => (
        <StyledPopoverContent>
          {/* Reapply the store theme when coloring the popover contents */}
          <KioskThemeProvider theme={storeTheme} themeId="quick-filter-button">
            <div id="quick-filter-button">
              <StyledList label="quantity selector" py={16}>
                {sortBy(options, 'label').map((option, index) => {
                  const isChecked = activeFilters.some(
                    (filter) => filter.value === option.value
                  );
                  return (
                    <QuickFilterItem
                      checked={isChecked}
                      disabled={option.count === 0}
                      isLastItem={index === options.length - 1}
                      key={`${option.value}-${isChecked}`}
                      onOptionSelect={onOptionSelect}
                      option={option}
                    />
                  );
                })}
              </StyledList>
              <Footer
                alignItems="center"
                flexDirection="row"
                justifyContent="space-between"
              >
                <Link
                  ariaLabel="Clear all filters"
                  color="grays-black"
                  onClick={() => onClearAll(label)}
                >
                  <Typography>Clear all</Typography>
                </Link>

                <Button
                  ariaLabel="view filtered products"
                  disabled={filteredProductCount === 0}
                  label={
                    filteredProductCount === undefined ? (
                      <Box px={16}>
                        <Loading color="white" />
                      </Box>
                    ) : (
                      <Typography
                        color="text-info"
                        variant="body-semibold"
                      >{`View (${filteredProductCount})`}</Typography>
                    )
                  }
                  onClick={() => {
                    trackFilteredProductList({
                      filterOptions: searchState,
                      filteredProductCount,
                      totalProductCount,
                      source: getSource({
                        pathname,
                        matches,
                        isTopper: false,
                      }),
                      dealName: null,
                    });
                    closePopover();
                  }}
                  size={isIpad ? 'small' : 'default'}
                  data-testid={`clear_all_${label.toLowerCase()}`}
                />
              </Footer>
            </div>
          </KioskThemeProvider>
        </StyledPopoverContent>
      )}
    </Popover>
  );
};
